// /police-clearance/$type/in/$location — Type × Location combo (TH, 2,500+ words)
import { policeComboSectionsTh, policeTypeSectionsTh } from "@/data/police-content";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { getPoliceType } from "@/data/police-clearance-types";
import { getLocation, locations } from "@/data/locations";
import { PoliceSections } from "@/components/PoliceSections";
import { POLICE_COMBO_LOC_SLUGS } from "@/routes/police-clearance.$type.index";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { RelatedComparisons } from "@/components/RelatedComparisons";
import { RelatedServicesInLocation } from "@/components/RelatedServicesInLocation";
import { MapPin, MessageCircle, Phone, ShieldCheck, BadgeCheck, ArrowRight } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

function isAllowedLoc(slug: string): boolean {
  return (POLICE_COMBO_LOC_SLUGS as readonly string[]).includes(slug);
}

export const Route = createFileRoute("/police-clearance/$type/in/$location")({
  loader: async ({ params }) => {
    if (!getPoliceType(params.type)) throw notFound();
    if (!getLocation(params.location)) throw notFound();
    if (!isAllowedLoc(params.location)) throw notFound();
    return { typeSlug: params.type, locSlug: params.location };
  },
  head: ({ params }) => {
    const t = getPoliceType(params.type);
    const loc = getLocation(params.location);
    if (!t || !loc) return { meta: [{ title: ctaTitle("ไม่พบหน้า", "/police-clearance/$type/in/$location", "th")}] };
    const url = `${SITE}/police-clearance/${t.slug}/in/${loc.slug}`;
    const shortLoc = loc.nameTh.replace("จังหวัด", "จ.").replace("เขต", "");
    const title = `${t.nameTh} ${shortLoc} ฿${t.priceFromTh.toLocaleString()} เสร็จ ${t.turnaround} | NYC`;
    const desc = `บริการ ${t.nameTh} (${t.nameEn}) สำหรับลูกค้า${loc.nameTh} · ${t.turnaround} · รับ-ส่งถึงพื้นที่ · LINE @nycli — ปรึกษาฟรี ทีมงานตอบไวทุกวัน`;
    return {
      meta: [
        { title: ctaTitle(title, "/police-clearance/$type/in/$location", "th") },
        { name: "description", content: ctaDesc(desc, "/police-clearance/$type/in/$location", "th")},
        { name: "keywords", content: `${t.nameTh} ${loc.nameTh}, ${t.nameEn} ${loc.nameEn}, Police Clearance ${loc.nameTh}, ตรวจประวัติ ${loc.nameTh}` },
        { property: "og:title", content: ctaTitle(title, "/police-clearance/$type/in/$location", "th")},
        { property: "og:description", content: ctaDesc(desc, "/police-clearance/$type/in/$location", "th")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "th_TH" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(`/police-clearance/${t.slug}/in/${loc.slug}`)],
      scripts: [
        consolidateGraph([
          speakableSchema(),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `${t.nameEn} in ${loc.nameEn}`,
              serviceType: "Police Clearance",
              areaServed: { "@type": "Place", name: loc.nameEn, ...(loc.lat && loc.lng ? { geo: { "@type": "GeoCoordinates", latitude: loc.lat, longitude: loc.lng } } : {}) },
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: t.priceFromTh, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Police Clearance", item: `${SITE}/police-clearance` },
                { "@type": "ListItem", position: 3, name: t.nameTh, item: `${SITE}/police-clearance/${t.slug}` },
                { "@type": "ListItem", position: 4, name: loc.nameTh, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 2).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) })),
        ]),
      ],
    };
  },
  component: PoliceComboPage,
});

function PoliceComboPage() {
  const { typeSlug, locSlug } = Route.useLoaderData() as { typeSlug: string; locSlug: string };
  const t = getPoliceType(typeSlug)!;
  const loc = getLocation(locSlug)!;
  // Compose: combo-specific + type-detail sections for 2,500+ word coverage
  const sections = [...policeComboSectionsTh(t, loc), ...policeTypeSectionsTh(t)];

  // Nearby allowed combo locations in same region (cap 6)
  const allowedSet = new Set(POLICE_COMBO_LOC_SLUGS as readonly string[]);
  const nearby = locations
    .filter((l) => allowedSet.has(l.slug) && l.slug !== loc.slug && l.region === loc.region)
    .slice(0, 6);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/police-clearance" className="hover:text-gold">Police Clearance</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/police-clearance/$type" params={{ type: typeSlug }} className="hover:text-gold">{t.nameTh}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{loc.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <MapPin className="h-3.5 w-3.5" /> {loc.nameTh}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            {t.nameTh} · {loc.nameTh}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            บริการ {t.nameTh} ({t.nameEn}) สำหรับลูกค้า{loc.nameTh} เริ่ม ฿{t.priceFromTh.toLocaleString()} · เสร็จ {t.turnaround}
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          NYC Legal & Notary Service — {t.nameTh} สำหรับลูกค้า{loc.nameTh} เริ่ม ฿{t.priceFromTh.toLocaleString()} เสร็จ {t.turnaround} โดยทีมงานขึ้นทะเบียนสภาทนายความฯ พร้อมรับ-ส่งเอกสารถึง{loc.region}
        </blockquote>

        <div className="mt-10">
          <PoliceSections sections={sections} />
        </div>

        <section className="mt-12">
          <ContactCTA />
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย ({loc.nameTh})</h2>
          <div className="mt-5">
            <FaqAccordion
              items={[
                { q: `${t.nameTh} ที่${loc.nameTh} ราคาเท่าไหร่?`, a: `เริ่ม ฿${t.priceFromTh.toLocaleString()} เสร็จ ${t.turnaround}` },
                { q: `ลูกค้า${loc.nameTh}ส่งเอกสารยังไง?`, a: `3 ทาง — EMS ถึงสำนักงาน, ทีมไปรับถึงที่ (${loc.region}), หรือสแกนส่งทาง LINE @nycli` },
                { q: `ใช้ได้กับประเทศไหนบ้าง?`, a: t.acceptedBy.join(", ") },
                { q: `Legalization ทำอย่างไร?`, a: t.legalizationPath },
              ]}
            />
          </div>
        </section>
      </div>

      {nearby.length > 0 && (
        <section className="border-t border-slate-200 bg-slate-50/50 py-12">
          <div className="mx-auto max-w-5xl px-4 sm:px-6">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
              {t.nameTh} · พื้นที่ใกล้เคียง ({loc.region})
            </h2>
            <p className="mt-2 text-sm text-slate-600">บริการ {t.nameTh} สำหรับพื้นที่อื่นใน{loc.region} — กดเลือกพื้นที่ใกล้คุณ</p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {nearby.map((n) => (
                <Link
                  key={n.slug}
                  to="/police-clearance/$type/in/$location"
                  params={{ type: t.slug, location: n.slug }}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
                >
                  <MapPin className="h-5 w-5 shrink-0 text-amber-600" />
                  <div className="flex-1">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                      {t.nameTh} {n.nameTh}
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-500">เริ่ม ฿{t.priceFromTh.toLocaleString()} · {t.turnaround}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-amber-600" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <RelatedServicesInLocation currentService="police" locationSlug={locSlug} locale="th" />
      <RelatedComparisons category="apostille" locale="th" />
      <TrustLayer variant="compact" />
          <QuotableStatsStrip cluster="police" locale="th" variant="light" />
    </main>
  );
}
